import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { absoluteUrl } from "@/lib/site-config";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import LegalTocMobile from "@/components/legal/LegalTocAside";
import LegalTocFixed from "@/components/legal/LegalTocFixed";
import { plans } from "@/data/pricing";
import { usePricing } from "@/context/PricingContext";

const purple = "#4A3A8A";
const lavender = "#f0eeff";
const textBody = "#3a3555";

function SectionShell({ id, num, title, children, className = "" }) {
  return (
    <section
      id={id}
      className={`scroll-mt-28 rounded-2xl border border-violet-light/80 bg-white p-6 shadow-sm sm:p-8 ${className}`}
    >
      <div className="mb-5 flex items-start gap-4 border-b border-violet-light/60 pb-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-fuerte text-sm font-bold text-white shadow-[0_4px_14px_-6px_rgba(74,58,138,0.5)]">
          {num}
        </div>
        <h2 className="pt-0.5 font-display text-lg font-bold leading-snug tracking-tight text-ink sm:text-[1.25rem]">
          {title}
        </h2>
      </div>
      <div className="text-[14.5px] leading-[1.78]" style={{ color: textBody }}>
        {children}
      </div>
    </section>
  );
}

function InfoBox({ accent = false, children }) {
  return (
    <div
      className="my-4 rounded-xl border px-5 py-4 text-[13.5px] leading-relaxed"
      style={{
        background: accent ? "rgba(201,162,39,0.07)" : lavender,
        borderColor: accent ? "rgba(201,162,39,0.35)" : "#e4dffe",
        borderLeft: `4px solid ${accent ? "#c9a227" : purple}`,
        color: accent ? "#5a4200" : textBody,
      }}
    >
      {children}
    </div>
  );
}

function AlertBox({ children }) {
  return (
    <div className="my-4 rounded-xl border border-red-200 border-l-4 border-l-red-500 bg-[#fff5f5] px-5 py-4 text-[13.5px] leading-relaxed text-[#7f1d1d]">
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="my-3 list-none space-y-2">
      {items.map((t, i) => (
        <li
          key={i}
          className="relative pl-5 text-[14.5px] leading-relaxed before:absolute before:left-0 before:top-[0.62rem] before:h-1.5 before:w-1.5 before:rounded-full before:bg-gold"
          style={{ color: textBody }}
        >
          {t}
        </li>
      ))}
    </ul>
  );
}

function CheckList({ items }) {
  return (
    <ul className="my-3 list-none space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2.5 text-[14px] leading-relaxed" style={{ color: textBody }}>
          <span className="shrink-0 pt-0.5 text-[13px] font-bold text-[#2e7d32]" aria-hidden>
            ✓
          </span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function CrossList({ items }) {
  return (
    <ul className="my-3 list-none space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2.5 text-[14px] leading-relaxed" style={{ color: textBody }}>
          <span className="shrink-0 pt-0.5 text-[13px] font-bold text-[#b91c1c]" aria-hidden>
            ✗
          </span>
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

const tocItems = [
  { href: "#s1", n: "1", label: "Identificación del proveedor" },
  { href: "#s2", n: "2", label: "Objeto del contrato" },
  { href: "#s3", n: "3", label: "Registro y cuenta" },
  { href: "#s4", n: "4", label: "Planes y precios" },
  { href: "#s5", n: "5", label: "Facturación y pagos" },
  { href: "#s6", n: "6", label: "Cancelación y reembolsos" },
  { href: "#s7", n: "7", label: "Uso permitido y prohibido" },
  { href: "#s8", n: "8", label: "Propiedad intelectual" },
  { href: "#s9", n: "9", label: "Datos y privacidad" },
  { href: "#s10", n: "10", label: "Disponibilidad y SLA" },
  { href: "#s11", n: "11", label: "Limitación de responsabilidad" },
  { href: "#s12", n: "12", label: "Modificaciones del servicio" },
  { href: "#s13", n: "13", label: "Suspensión y resolución" },
  { href: "#s14", n: "14", label: "Ley aplicable y jurisdicción" },
  { href: "#s15", n: "15", label: "Contacto" },
];

const refundRows = [
  { situation: "Cancelación dentro de los primeros 14 días tras la contratación inicial", refund: "Reembolso completo del primer pago" },
  { situation: "Cancelación tras 14 días del período inicial", refund: "Sin reembolso. Acceso hasta fin del período" },
  { situation: "Interrupción del servicio por causa imputable a EkklesiaOnline (>24h)", refund: "Crédito proporcional en la siguiente factura" },
  { situation: "Plan anual cancelado antes de completar el año", refund: "Sin reembolso de los meses restantes" },
];

const slaRows = [
  { tipo: "Servicio caído (crítico)", respuesta: "2 horas", resolucion: "8 horas" },
  { tipo: "Funcionalidad principal afectada", respuesta: "4 horas hábiles", resolucion: "48 horas hábiles" },
  { tipo: "Incidencia menor o consulta", respuesta: "48 horas hábiles", resolucion: "5 días hábiles" },
];

export default function TermsConditionsPage() {
  const sentinelRef = useRef(null);
  // Precios servidos por el backend (no hardcodeados en el frontend).
  const { prices } = usePricing();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#f4f3f9] text-ink">
      <Seo
        title="Términos y condiciones"
        description="Términos y condiciones de uso de EkklesiaOnline: contrato SaaS, planes, facturación, cancelación, propiedad intelectual, privacidad, SLA y ley aplicable."
        path="/terminos-condiciones"
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Términos y condiciones | Ekklesia",
          description: "Contrato de uso del servicio EkklesiaOnline para iglesias y entidades.",
          url: absoluteUrl("/terminos-condiciones"),
          isPartOf: { "@type": "WebSite", name: "Ekklesia", url: absoluteUrl("/") },
        }}
      />
      <Header />

      <main className="mx-auto max-w-7xl px-4 pb-28 pt-24 sm:px-6 sm:pt-28 md:px-10 md:pt-32 lg:px-12">
        <div className="lg:pr-[calc(16rem+2.5rem)]">
          <header>
            <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-gold">Legal</p>
            <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-[2.2rem] lg:leading-tight">
              Términos y Condiciones
            </h1>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-2">
              Contrato de uso del servicio EkklesiaOnline entre el proveedor y las iglesias o entidades que contratan la
              plataforma.
            </p>
            <div className="mt-7 grid gap-3 sm:grid-cols-3 sm:gap-4">
              {[
                { label: "Actualización", value: "Abril 2026" },
                { label: "Proveedor", value: "Osmel Enamorado" },
                { label: "Ley aplicable", value: "Derecho español" },
              ].map((c) => (
                <div key={c.label} className="rounded-xl border border-violet-light/80 bg-white/90 px-4 py-3 shadow-sm">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-ink-4">{c.label}</p>
                  <p className="mt-1 font-semibold text-ink">{c.value}</p>
                </div>
              ))}
            </div>
          </header>

          {/* Resumen ejecutivo */}
          <div
            className="mt-10 rounded-2xl border border-violet-light/40 p-6 text-white shadow-md sm:p-8"
            style={{ background: "linear-gradient(145deg, #32276b 0%, #4a3a8a 100%)" }}
          >
            <h2 className="font-display text-[15px] font-bold uppercase tracking-[0.08em] text-[#e8c04a]">
              Resumen para pastores: Lo más importante
            </h2>
            <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
              {[
                { icon: "💳", title: "Pago mensual o anual", text: "Facturación automática. Puedes cancelar cuando quieras sin penalización." },
                { icon: "📦", title: "Tus datos son tuyos", text: "Puedes exportarlos en cualquier momento. Si cancelas, tienes 30 días para descargarlos." },
                { icon: "🔒", title: "Privacidad garantizada", text: "Los datos de tu congregación no se comparten ni venden a terceros bajo ningún concepto." },
                { icon: "🛠️", title: "Soporte incluido", text: "Asistencia técnica por correo incluida en todos los planes. Respondemos en menos de 48h hábiles." },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex gap-3 rounded-xl px-4 py-3.5"
                  style={{ background: "rgba(255,255,255,0.07)" }}
                >
                  <span className="text-lg" aria-hidden>
                    {item.icon}
                  </span>
                  <div>
                    <strong className="block text-sm text-white">{item.title}</strong>
                    <p className="mt-0.5 text-[13.5px] leading-snug text-white/80">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <LegalTocMobile ariaLabel="Índice de términos y condiciones" tocItems={tocItems} />

          <div className="mt-10 space-y-6">
            <SectionShell id="s1" num="1" title="Identificación del proveedor">
              <p className="mb-4">
                El presente contrato regula el acceso y uso de la plataforma <strong className="text-violet-oscuro">EkklesiaOnline</strong>,
                titularidad de:
              </p>
              <BulletList
                items={[
                  <span key="t">
                    <strong className="text-violet-oscuro">Titular:</strong> Osmel Enamorado (autónomo registrado en España)
                  </span>,
                  <span key="n">
                    <strong className="text-violet-oscuro">Nombre comercial:</strong> EkklesiaOnline
                  </span>,
                  <span key="w">
                    <strong className="text-violet-oscuro">Sitio web:</strong> ekklesiaonline.app
                  </span>,
                  <span key="e">
                    <strong className="text-violet-oscuro">Correo de contacto:</strong> hola@ekklesiaonline.app
                  </span>,
                  <span key="p">
                    <strong className="text-violet-oscuro">País de operación:</strong> España
                  </span>,
                ]}
              />
              <p className="mt-4">
                La aceptación de estos Términos y Condiciones tiene carácter vinculante y constituye un contrato entre el
                proveedor y el <strong className="text-violet-oscuro">Cliente</strong> (la persona física o entidad religiosa
                que registra una cuenta y contrata el servicio).
              </p>
            </SectionShell>

            <SectionShell id="s2" num="2" title="Objeto del contrato">
              <p className="mb-4">
                EkklesiaOnline es una plataforma de software como servicio (SaaS) diseñada para facilitar la gestión integral
                de iglesias y comunidades de fe de habla hispana. El servicio incluye, entre otros módulos:
              </p>
              <BulletList
                items={[
                  "Gestión de miembros y congregación",
                  "Seguimiento de integración y discipulado",
                  "Gestión de ministerios y equipos",
                  "Calendario de eventos y actividades",
                  "Módulo de finanzas (diezmos y ofrendas)",
                  "Sistema de notificaciones inteligentes (Sophía)",
                  "Informes y estadísticas de la iglesia",
                ]}
              />
              <p className="mt-4">
                El acceso al servicio se realiza a través de internet mediante navegador web o aplicación móvil, bajo un
                modelo de suscripción periódica. EkklesiaOnline se compromete a prestar el servicio con la diligencia debida y
                a mantenerlo actualizado y funcional.
              </p>
              <InfoBox>
                <strong className="text-violet-fuerte">Naturaleza del servicio:</strong> EkklesiaOnline es una herramienta de
                gestión. No constituye asesoramiento teológico, jurídico, contable ni pastoral. La iglesia es responsable de
                las decisiones que adopte basándose en la información gestionada en la plataforma.
              </InfoBox>
            </SectionShell>

            <SectionShell id="s3" num="3" title="Registro y cuenta">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">3.1 Requisitos de registro</h3>
              <p className="mb-3">
                Para acceder al servicio, el Cliente debe crear una cuenta proporcionando información veraz, completa y
                actualizada. El registro está disponible para:
              </p>
              <BulletList
                items={[
                  "Personas físicas mayores de 18 años que actúen en representación de una iglesia o entidad religiosa.",
                  "Entidades religiosas legalmente constituidas o en proceso de constitución.",
                ]}
              />
              <h3 className="mb-3 mt-6 font-display text-[15px] font-bold text-violet-fuerte">3.2 Responsabilidad de la cuenta</h3>
              <p className="mb-3">
                El Cliente es responsable de mantener la confidencialidad de sus credenciales de acceso y de todas las
                actividades realizadas desde su cuenta. Deberá notificar a EkklesiaOnline de forma inmediata ante cualquier
                uso no autorizado en la dirección <strong className="text-violet-oscuro">hola@ekklesiaonline.app</strong>.
              </p>
              <p>EkklesiaOnline no será responsable de los daños derivados del incumplimiento de esta obligación de custodia.</p>
              <h3 className="mb-3 mt-6 font-display text-[15px] font-bold text-violet-fuerte">3.3 Usuarios adicionales</h3>
              <p>
                Dependiendo del plan contratado, el Cliente podrá crear cuentas de usuario adicionales para su equipo pastoral
                o administrativo. Cada usuario adicional queda sujeto a estos mismos Términos y Condiciones. El administrador
                principal es responsable del uso que hagan estos usuarios de la plataforma.
              </p>
            </SectionShell>

            <SectionShell id="s4" num="4" title="Planes y precios">
              <p className="mb-5">
                EkklesiaOnline ofrece diferentes planes de suscripción adaptados al tamaño y necesidades de cada iglesia. Los
                precios vigentes están siempre disponibles en{" "}
                <Link to="/#pricing" className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro">
                  la sección de precios
                </Link>
                .
              </p>
              <div className="grid gap-3.5 sm:grid-cols-3">
                {plans.map((plan) => {
                  const caps =
                    plan.features[0] && plan.features[1]
                      ? `${plan.features[0]} · ${plan.features[1]}`
                      : null;
                  const highlighted = plan.highlight;
                  // Precio mensual de referencia desde el backend (IVA incluido).
                  const monthly = prices?.[plan.name.toLowerCase()]?.monthly ?? null;
                  return (
                    <div
                      key={plan.name}
                      className={`rounded-xl px-4 py-4 text-center ${
                        highlighted
                          ? "border-2 border-violet-fuerte"
                          : "border border-violet-light/80"
                      }`}
                      style={highlighted ? { background: lavender } : undefined}
                    >
                      <p className="text-[13px] font-bold uppercase tracking-wide text-violet-fuerte">{plan.name}</p>
                      <p className="mt-2 font-display text-2xl font-bold text-violet-oscuro">
                        {monthly != null ? (
                          <>
                            {plan.prefix}
                            {monthly}
                            &nbsp;€ <span className="text-sm font-medium text-ink-3">/ mes</span>
                          </>
                        ) : (
                          <Link
                            to="/#pricing"
                            className="text-base font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro"
                          >
                            Ver precios
                          </Link>
                        )}
                      </p>
                      <p className="mt-2 text-[12.5px] leading-snug text-ink-3">{plan.desc}</p>
                      {caps ? (
                        <p className="mt-2 border-t border-violet-light/50 pt-2 text-[11.5px] leading-snug text-ink-4">{caps}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <InfoBox>
                <strong className="text-violet-fuerte">Nota:</strong> Las cifras corresponden a la facturación mensual de
                referencia con IVA incluido. El pago anual (con su equivalente mensual), promociones vigentes (por ejemplo,
                para las primeras iglesias), módulos opcionales y el desglose final se muestran en{" "}
                <Link to="/#pricing" className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro">
                  la sección de precios
                </Link>{" "}
                y en el checkout. EkklesiaOnline puede modificar sus tarifas con un preaviso mínimo de 30 días.
              </InfoBox>
              <h3 className="mb-3 mt-6 font-display text-[15px] font-bold text-violet-fuerte">4.1 Período de prueba</h3>
              <p>
                EkklesiaOnline puede ofrecer períodos de prueba gratuita para nuevos clientes. Durante este período, el
                Cliente tiene acceso completo al plan correspondiente sin coste. Al finalizar la prueba, si el Cliente no
                cancela, se iniciará la facturación según el plan seleccionado.
              </p>
            </SectionShell>

            <SectionShell id="s5" num="5" title="Facturación y pagos">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">5.1 Ciclo de facturación</h3>
              <p className="mb-4">
                El servicio se factura de forma recurrente, mensual o anual según la opción escogida en el momento de la
                contratación. La facturación se realiza por adelantado al inicio de cada período.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">5.2 Procesamiento de pagos</h3>
              <p className="mb-4">
                Los pagos se procesan de forma segura a través de <strong className="text-violet-oscuro">Stripe</strong>,
                procesador certificado PCI-DSS nivel 1. EkklesiaOnline no almacena datos de tarjetas de crédito o débito. El
                Cliente acepta también los{" "}
                <a
                  href="https://stripe.com/es/legal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro"
                >
                  Términos de Servicio de Stripe
                </a>
                .
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">5.3 Impuestos</h3>
              <p className="mb-4">
                Los precios mostrados incluyen el IVA español vigente (21%). En la factura correspondiente se desglosará la
                base imponible y la cuota de IVA. Los clientes establecidos fuera de España en la Unión Europea pueden estar
                sujetos a normativa de IVA transfronterizo.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">5.4 Facturas</h3>
              <p className="mb-4">
                EkklesiaOnline emitirá una factura electrónica por cada cobro realizado, que se enviará automáticamente al
                correo del administrador registrado en la cuenta.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">5.5 Impago</h3>
              <p>
                En caso de que el cobro no pueda procesarse, EkklesiaOnline notificará al Cliente y realizará hasta tres
                reintentos en un plazo de 7 días. Si el impago persiste, el acceso a la plataforma podrá suspenderse
                temporalmente hasta regularizar la situación. Los datos del Cliente se conservarán durante este período.
              </p>
            </SectionShell>

            <SectionShell id="s6" num="6" title="Cancelación y reembolsos">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">6.1 Cancelación por el Cliente</h3>
              <p className="mb-3">
                El Cliente puede cancelar su suscripción en cualquier momento desde el panel de <strong className="text-violet-oscuro">Ajustes → Suscripción</strong> de su cuenta, sin necesidad de contactar con soporte y sin penalización alguna.
              </p>
              <BulletList
                items={[
                  "La cancelación tiene efecto al final del período de facturación en curso.",
                  "El acceso al servicio se mantiene activo hasta ese momento.",
                  "Tras la cancelación, el Cliente dispone de 30 días para exportar todos sus datos.",
                  "Transcurrido ese plazo, los datos serán eliminados de forma permanente.",
                ]}
              />
              <h3 className="mb-3 mt-6 font-display text-[15px] font-bold text-violet-fuerte">6.2 Política de reembolsos</h3>
              <div className="overflow-x-auto rounded-xl border border-violet-light/80">
                <table className="w-full min-w-[480px] border-collapse text-[13.5px]">
                  <thead>
                    <tr className="bg-violet-fuerte text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Situación</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Reembolso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refundRows.map((row) => (
                      <tr key={row.situation} className="border-b border-violet-light/60 last:border-b-0 hover:bg-violet-light/30">
                        <td className="px-4 py-3 align-top font-medium text-violet-oscuro">{row.situation}</td>
                        <td className="px-4 py-3 align-top" style={{ color: textBody }}>
                          {row.refund}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <InfoBox accent>
                <strong className="text-[#7a5800]">Derecho de desistimiento:</strong> Conforme a la normativa española de
                consumidores (RDL 1/2007), el Cliente dispone de 14 días naturales desde la contratación para desistir sin
                necesidad de justificación, con derecho a reembolso completo, siempre que no haya hecho uso efectivo del
                servicio durante ese período.
              </InfoBox>
            </SectionShell>

            <SectionShell id="s7" num="7" title="Uso permitido y prohibido">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">El Cliente puede:</h3>
              <CheckList
                items={[
                  "Utilizar la plataforma para la gestión interna de su iglesia o comunidad de fe.",
                  "Introducir y gestionar datos de los miembros de su congregación.",
                  "Exportar sus propios datos en cualquier momento.",
                  "Ceder el acceso a colaboradores pastorales o administrativos dentro de su iglesia.",
                  "Utilizar EkklesiaOnline para múltiples congregaciones si el plan lo permite.",
                ]}
              />
              <h3 className="mb-3 mt-6 font-display text-[15px] font-bold text-violet-fuerte">El Cliente no puede:</h3>
              <CrossList
                items={[
                  "Revender, sublicenciar o ceder el servicio a terceros ajenos a su iglesia.",
                  "Utilizar la plataforma para actividades ilegales, discriminatorias o contrarias al orden público.",
                  "Intentar acceder a datos de otras iglesias o cuentas ajenas.",
                  "Realizar ingeniería inversa, descompilar o copiar el código fuente de la plataforma.",
                  "Automatizar el acceso mediante bots o scripts no autorizados.",
                  "Introducir datos de personas sin su conocimiento o consentimiento.",
                  "Utilizar la plataforma para finalidades distintas a la gestión eclesiástica.",
                ]}
              />
              <AlertBox>
                <strong className="text-[#b91c1c]">Incumplimiento:</strong> El incumplimiento de las restricciones de uso puede
                dar lugar a la suspensión inmediata de la cuenta, sin derecho a reembolso, y a las acciones legales que
                correspondan.
              </AlertBox>
            </SectionShell>

            <SectionShell id="s8" num="8" title="Propiedad intelectual">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">8.1 Propiedad de EkklesiaOnline</h3>
              <p className="mb-3">
                Todo el software, diseño, código fuente, logotipos, marca, textos, interfaces y funcionalidades de
                EkklesiaOnline son propiedad exclusiva de Osmel Enamorado y están protegidos por la legislación española e
                internacional sobre propiedad intelectual e industrial.
              </p>
              <p className="mb-4">
                La contratación del servicio otorga al Cliente una{" "}
                <strong className="text-violet-oscuro">licencia de uso limitada, no exclusiva, intransferible y revocable</strong>{" "}
                para acceder y utilizar la plataforma durante el período de suscripción vigente, exclusivamente para los fines
                descritos en estos Términos.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">8.2 Propiedad de los datos del Cliente</h3>
              <p className="mb-3">
                El Cliente conserva en todo momento la <strong className="text-violet-oscuro">plena propiedad</strong> sobre
                todos los datos que introduce en la plataforma (información de miembros, finanzas, eventos, etc.). EkklesiaOnline
                no adquiere ningún derecho de propiedad sobre dichos datos.
              </p>
              <p>
                El Cliente otorga a EkklesiaOnline una licencia limitada para tratar dichos datos únicamente con el fin de
                prestar el servicio contratado.
              </p>
            </SectionShell>

            <SectionShell id="s9" num="9" title="Datos y privacidad">
              <p className="mb-4">
                El tratamiento de datos personales se rige por la <strong className="text-violet-oscuro">Política de Privacidad</strong> de EkklesiaOnline, disponible en{" "}
                <Link to="/privacidad" className="font-semibold text-violet-fuerte underline underline-offset-2 hover:text-violet-oscuro">
                  esta página
                </Link>
                , que forma parte integrante de estos Términos y Condiciones.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">9.1 Acuerdo de encargo del tratamiento (DPA)</h3>
              <p className="mb-3">
                En relación con los datos de los miembros de la congregación que el Cliente introduce en la plataforma, las
                partes acuerdan que:
              </p>
              <BulletList
                items={[
                  <span key="a">
                    El <strong className="text-violet-oscuro">Cliente</strong> (la iglesia) actúa como{" "}
                    <strong className="text-violet-oscuro">Responsable del Tratamiento</strong>.
                  </span>,
                  <span key="b">
                    <strong className="text-violet-oscuro">EkklesiaOnline</strong> actúa como{" "}
                    <strong className="text-violet-oscuro">Encargado del Tratamiento</strong>.
                  </span>,
                  "EkklesiaOnline tratará dichos datos exclusivamente siguiendo las instrucciones del Cliente y conforme al RGPD.",
                  "EkklesiaOnline no comunicará estos datos a terceros salvo obligación legal o instrucción expresa del Cliente.",
                ]}
              />
              <InfoBox>
                <strong className="text-violet-fuerte">DPA completo:</strong> Las iglesias que requieran un Acuerdo de Encargo
                del Tratamiento (Data Processing Agreement) firmado para cumplir con sus obligaciones legales pueden
                solicitarlo en <strong className="text-violet-oscuro">privacidad@ekklesiaonline.app</strong>.
              </InfoBox>
            </SectionShell>

            <SectionShell id="s10" num="10" title="Disponibilidad del servicio y SLA">
              <p className="mb-4">
                EkklesiaOnline se compromete a ofrecer una disponibilidad mínima del <strong className="text-violet-oscuro">99% mensual</strong>, excluyendo mantenimientos programados y causas de fuerza mayor.
              </p>
              <div className="overflow-x-auto rounded-xl border border-violet-light/80">
                <table className="w-full min-w-[520px] border-collapse text-[13.5px]">
                  <thead>
                    <tr className="bg-violet-fuerte text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Tipo de incidencia</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Tiempo de respuesta</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Tiempo de resolución</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slaRows.map((row) => (
                      <tr key={row.tipo} className="border-b border-violet-light/60 last:border-b-0 hover:bg-violet-light/30">
                        <td className="px-4 py-3 align-top font-medium text-violet-oscuro">{row.tipo}</td>
                        <td className="px-4 py-3 align-top" style={{ color: textBody }}>
                          {row.respuesta}
                        </td>
                        <td className="px-4 py-3 align-top" style={{ color: textBody }}>
                          {row.resolucion}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Los mantenimientos programados se notificarán con un mínimo de <strong className="text-violet-oscuro">48 horas de antelación</strong> y se realizarán preferentemente en horario de baja demanda (madrugada, hora española).
              </p>
              <InfoBox accent>
                <strong className="text-[#7a5800]">Compensación por incidencias:</strong> Si la disponibilidad mensual cae por
                debajo del 99% por causas imputables a EkklesiaOnline, el Cliente recibirá un crédito equivalente a los días de
                servicio afectados, aplicado en su siguiente factura.
              </InfoBox>
            </SectionShell>

            <SectionShell id="s11" num="11" title="Limitación de responsabilidad">
              <p className="mb-3">EkklesiaOnline no será responsable de:</p>
              <BulletList
                items={[
                  "Pérdidas de datos causadas por el uso incorrecto de la plataforma por parte del Cliente.",
                  "Daños indirectos, pérdida de beneficios o daños consecuentes derivados del uso o imposibilidad de uso del servicio.",
                  "Interrupciones causadas por terceros proveedores de infraestructura (MongoDB Atlas, Vercel, etc.) o por causas de fuerza mayor.",
                  "Decisiones pastorales, administrativas o financieras adoptadas por la iglesia basándose en los datos gestionados en la plataforma.",
                  "Incumplimiento de las obligaciones de protección de datos por parte del Cliente como Responsable del Tratamiento.",
                ]}
              />
              <p className="mt-4">
                En cualquier caso, la responsabilidad máxima de EkklesiaOnline frente al Cliente quedará limitada al importe
                abonado por el Cliente en los <strong className="text-violet-oscuro">3 meses anteriores</strong> al hecho
                generador del daño.
              </p>
            </SectionShell>

            <SectionShell id="s12" num="12" title="Modificaciones del servicio y condiciones">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">12.1 Modificaciones del servicio</h3>
              <p className="mb-4">
                EkklesiaOnline se reserva el derecho de añadir, modificar o eliminar funcionalidades de la plataforma con el
                objetivo de mejorar el servicio. Los cambios significativos se comunicarán con al menos{" "}
                <strong className="text-violet-oscuro">15 días de antelación</strong>.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">12.2 Modificaciones de precios</h3>
              <p className="mb-4">
                EkklesiaOnline podrá modificar sus tarifas con un preaviso mínimo de <strong className="text-violet-oscuro">30 días</strong> mediante notificación al correo registrado. Si el Cliente no acepta el nuevo precio, podrá cancelar su suscripción antes de la entrada en vigor del cambio sin penalización.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">12.3 Modificaciones de estos Términos</h3>
              <p>
                EkklesiaOnline podrá actualizar estos Términos y Condiciones notificándolo con al menos{" "}
                <strong className="text-violet-oscuro">15 días de antelación</strong>. El uso continuado del servicio tras la
                entrada en vigor implicará la aceptación de los nuevos términos. Si el Cliente no está de acuerdo, podrá
                cancelar su cuenta.
              </p>
            </SectionShell>

            <SectionShell id="s13" num="13" title="Suspensión y resolución del contrato">
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">13.1 Suspensión por impago</h3>
              <p className="mb-4">
                Tras 7 días sin regularizar un impago, EkklesiaOnline podrá suspender temporalmente el acceso a la plataforma.
                Los datos se conservan íntegros durante un período de 30 días adicionales.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">13.2 Resolución por incumplimiento</h3>
              <p className="mb-3">
                EkklesiaOnline se reserva el derecho de resolver el contrato y dar de baja la cuenta de forma inmediata, sin
                reembolso, en los siguientes casos:
              </p>
              <BulletList
                items={[
                  "Uso de la plataforma para actividades ilegales o contrarias a estos Términos.",
                  "Intento de acceso no autorizado a sistemas o datos ajenos.",
                  "Impago persistente tras los avisos correspondientes.",
                  "Proporcionar información falsa en el proceso de registro.",
                ]}
              />
              <h3 className="mb-3 mt-6 font-display text-[15px] font-bold text-violet-fuerte">13.3 Resolución por el Cliente</h3>
              <p className="mb-4">
                El Cliente puede resolver el contrato en cualquier momento siguiendo el proceso de cancelación descrito en el
                apartado 6, sin necesidad de justificación.
              </p>
              <h3 className="mb-3 font-display text-[15px] font-bold text-violet-fuerte">13.4 Cierre del servicio por EkklesiaOnline</h3>
              <p>
                En el caso excepcional de que EkklesiaOnline decida cesar la prestación del servicio, notificará a todos los
                Clientes con un mínimo de <strong className="text-violet-oscuro">90 días de antelación</strong>, facilitando las
                herramientas necesarias para exportar la totalidad de sus datos, y reembolsará el tiempo prepagado no
                consumido.
              </p>
            </SectionShell>

            <SectionShell id="s14" num="14" title="Ley aplicable y jurisdicción">
              <p className="mb-3">El presente contrato se rige por el <strong className="text-violet-oscuro">derecho español</strong>, en particular por:</p>
              <BulletList
                items={[
                  "Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE)",
                  "Real Decreto Legislativo 1/2007, de la Ley General para la Defensa de los Consumidores y Usuarios",
                  "Reglamento (UE) 2016/679 (RGPD) y Ley Orgánica 3/2018 (LOPDGDD)",
                  "Código Civil y legislación mercantil española aplicable",
                ]}
              />
              <p className="mt-4">
                Para la resolución de cualquier controversia derivada de este contrato, las partes se someten, con renuncia
                expresa a cualquier otro fuero, a los <strong className="text-violet-oscuro">Juzgados y Tribunales de España</strong>.
              </p>
              <InfoBox>
                <strong className="text-violet-fuerte">Resolución extrajudicial:</strong> Antes de acudir a la vía judicial,
                EkklesiaOnline se compromete a intentar resolver cualquier disputa de forma amistosa. El Cliente puede
                contactar en <strong className="text-violet-oscuro">hola@ekklesiaonline.app</strong> para iniciar este proceso.
              </InfoBox>
            </SectionShell>

            <SectionShell id="s15" num="15" title="Contacto">
              <div
                className="rounded-xl border border-violet-light/30 p-6 text-white sm:p-8"
                style={{ background: "linear-gradient(135deg, #32276b, #4a3a8a)" }}
              >
                <h3 className="mb-3 font-display text-base font-bold text-[#e8c04a]">¿Preguntas sobre estos Términos?</h3>
                <p className="text-[14px] leading-relaxed text-white/75">
                  Si tienes cualquier duda sobre el contrato o el funcionamiento de EkklesiaOnline, estamos aquí para ayudarte:
                </p>
                <p className="mt-4 text-[14px] leading-relaxed text-white/75">
                  <strong className="text-white">Proveedor:</strong> Osmel Enamorado
                  <br />
                  <strong className="text-white">Servicio:</strong> EkklesiaOnline
                  <br />
                  <strong className="text-white">Web:</strong>{" "}
                  <a href="https://ekklesiaonline.app" className="font-semibold text-[#e8c04a] underline-offset-2 hover:underline">
                    ekklesiaonline.app
                  </a>
                  <br />
                  <strong className="text-white">Correo general:</strong>{" "}
                  <a href="mailto:hola@ekklesiaonline.app" className="font-semibold text-[#e8c04a] underline-offset-2 hover:underline">
                    hola@ekklesiaonline.app
                  </a>
                  <br />
                  <strong className="text-white">Correo privacidad:</strong>{" "}
                  <a href="mailto:privacidad@ekklesiaonline.app" className="font-semibold text-[#e8c04a] underline-offset-2 hover:underline">
                    privacidad@ekklesiaonline.app
                  </a>
                </p>
                <p className="mt-4 text-[13px] text-white/50">
                  Nos comprometemos a responder cualquier consulta en un plazo máximo de 48 horas hábiles.
                </p>
              </div>
            </SectionShell>

            <p className="rounded-xl border border-violet-light/60 bg-white/80 px-5 py-4 text-center text-[13px] text-ink-3">
              Documento elaborado conforme a la legislación española y al Reglamento (UE) 2016/679 (RGPD).
            </p>

            <div ref={sentinelRef} aria-hidden />
          </div>
        </div>
      </main>

      <LegalTocFixed sentinelRef={sentinelRef} ariaLabel="Índice de términos y condiciones" tocItems={tocItems} />

      <Footer />
    </div>
  );
}
